/**
 * version-watch — сторож версии оболочки: человек обязан увидеть новую сборку САМ,
 * без убийства приложения и чистки кэша.
 *
 * ПОЧЕМУ (разнос ректора 21 авг 2026): «сделай так, чтобы мне не приходилось выбивать
 * Telegram из процессов, перезагружать телефон и чистить кэш, чтобы увидеть новую версию.
 * Видимо, наши пользователи её вообще никогда не увидят».
 *
 * И он прав: раздача настроена верно (index.html отдаётся без кэша), но это помогает
 * только при НОВОЙ загрузке страницы. Оболочка мини-аппа в Telegram-WebView живёт
 * сутками: человек сворачивает и возвращается в ТУ ЖЕ страницу со старым бандлом.
 * Service Worker при этом обновляется молча — и всё равно ничего не меняет, потому что
 * уже загруженный документ продолжает работать на старом коде. Понять, что оболочка
 * устарела, приложению было НЕЧЕМ.
 *
 * ЧТО ЗДЕСЬ. Два независимых механизма — второй нужен там, где Service Worker мёртв
 * (iOS-webview, отключённые SW):
 *   1. `/build.json` с идентификатором текущей сборки, запрашиваемый мимо всех кэшей.
 *      Не совпал с тем, что вписан в загруженную страницу, — оболочка старая.
 *   2. `registration.update()` — пинок самому Service Worker, чтобы он подтянул новый
 *      precache и не отдавал старый index.html из своего хранилища.
 *
 * КОГДА ПРОВЕРЯЕМ: при старте, при каждом возврате в приложение (visibilitychange) и раз
 * в четверть часа, пока оно открыто. Возврат — главный момент: человек только что был
 * снаружи, контекст терять не жалко, и обновление проходит незаметно.
 *
 * КАК ОБНОВЛЯЕМ: если человек вернулся в приложение — перезагружаем сразу и молча.
 * Если он в этот момент читает (страница видима давно) — не выдёргиваем экран из-под
 * рук, а ставим флаг и обновляемся при следующем возврате.
 */

const BUILD_URL = '/build.json';
/** Метка «за этой версией мы уже перезагружались» — предохранитель от петли. */
const TRIED_KEY = 'padla:build-reload';
const POLL_MS = 15 * 60 * 1000;
/** Возврат в приложение — окно, в котором обновление незаметно (человек ещё не читает). */
const FRESH_RETURN_MS = 2500;

interface BuildStamp {
  build?: string;
}

/**
 * Идентификатор сборки, в которой работает эта страница: мета-тег `padla-build`, который сборка вписывает в
 * index.html (vite.config.ts, padla-build-stamp; отпечаток содержимого сборки — 30 сен 2026). Нет тега (dev-сервер) —
 * сторож молчит.
 */
function currentBuildId(): string {
  try {
    return document.querySelector('meta[name="padla-build"]')?.getAttribute('content') ?? '';
  } catch {
    return '';
  }
}

async function fetchLiveBuildId(): Promise<string | null> {
  try {
    // no-store + метка времени: мимо SW, мимо HTTP-кэша, мимо кэша WebView.
    const res = await fetch(`${BUILD_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as BuildStamp;
    return typeof body.build === 'string' && body.build ? body.build : null;
  } catch {
    return null; // сеть отвалилась — не наше дело, сторож молчит
  }
}

export interface VersionWatchOptions {
  /** Пинок Service Worker (из registerSW). Нет — работает только сетевая проверка. */
  updateServiceWorker?: () => void | Promise<void>;
  /** Перезагрузка (подменяется в тесте). */
  reload?: () => void;
  /** Метрика — чтобы видеть, что обновления реально доезжают до людей. */
  onOutcome?: (outcome: 'fresh' | 'stale-reload' | 'stale-deferred' | 'stale-stuck') => void;
}

/**
 * Предохранитель от петли: если сеть отдаёт новую метку, а страница после
 * перезагрузки приходит со старым бандлом (кэш на пути, застрявший CDN), без этого
 * телефон человека уходил бы в вечную перезагрузку. Пробуем РОВНО ОДИН раз на метку.
 */
function alreadyTried(live: string): boolean {
  try {
    return window.sessionStorage.getItem(TRIED_KEY) === live;
  } catch {
    return false; // приватный режим / запрет хранилища — просто без предохранителя
  }
}
function rememberTried(live: string): void {
  try {
    window.sessionStorage.setItem(TRIED_KEY, live);
  } catch {
    /* хранилище недоступно — не наша беда */
  }
}
function forgetTried(): void {
  try {
    window.sessionStorage.removeItem(TRIED_KEY);
  } catch {
    /* см. выше */
  }
}

export function installVersionWatch(opts: VersionWatchOptions = {}): () => void {
  const reload = opts.reload ?? ((): void => window.location.reload());
  const mine = currentBuildId();
  let staleSeen = false;
  let lastLive = '';
  let becameVisibleAt = Date.now();
  let stopped = false;

  const check = async (): Promise<void> => {
    if (stopped) return;
    void opts.updateServiceWorker?.();
    const live = await fetchLiveBuildId();
    if (!live || !mine || live === mine) {
      if (live && mine && live === mine) {
        forgetTried();
        opts.onOutcome?.('fresh');
      }
      return;
    }
    // Уже перезагружались ради этой версии, а бандл прежний — второй раз не дёргаем.
    if (alreadyTried(live)) {
      opts.onOutcome?.('stale-stuck');
      return;
    }
    staleSeen = true;
    lastLive = live;
    // Человек только что вернулся в приложение — обновляемся молча, терять нечего.
    if (document.visibilityState === 'visible' && Date.now() - becameVisibleAt < FRESH_RETURN_MS) {
      rememberTried(live);
      opts.onOutcome?.('stale-reload');
      reload();
      return;
    }
    opts.onOutcome?.('stale-deferred');
  };

  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    becameVisibleAt = Date.now();
    // Оболочка уже знает, что устарела, — момент возврата и есть время обновиться.
    if (staleSeen) {
      if (lastLive) rememberTried(lastLive);
      opts.onOutcome?.('stale-reload');
      reload();
      return;
    }
    void check();
  };

  document.addEventListener('visibilitychange', onVisibility);
  const timer = window.setInterval(() => void check(), POLL_MS);
  void check();

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearInterval(timer);
  };
}
